// Lot 723 — annulation d'une occurrence de culte depuis la page d'église (responsable / gestionnaire).
// Lot 724 — ou déplacement ponctuel (autre heure / autre jour) avec notification « Culte déplacé ».
import React, { useState } from "react";
import { CalendarX2, CalendarClock, RotateCcw, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { fmtShortDate, fmtIsoDate, fmtDM, localDateIso } from "@/lib/churchServices";

const RED = "#B91C1C";
const AMBER = "#B45309";

// Boîte « Annuler / Déplacer ce culte ? » : bascule de mode, raison facultative, interrupteur « Prévenir les membres » (ON).
export const CancelServiceDialog = ({ churchId, target, onClose, onDone }) => {
  const { t, lang } = useApp();
  const [mode, setMode] = useState("cancel");
  const [reason, setReason] = useState("");
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [newDate, setNewDate] = useState(() => (target?.date ? localDateIso(target.date) : ""));
  const [newTime, setNewTime] = useState(() => target?.item?.time || "");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const { item, date } = target;
  const dateLabel = fmtShortDate(date, lang);
  const title = item.title || t("church.service", "Culte");
  const moving = mode === "move";
  const sameSlot = moving && newDate === localDateIso(date) && newTime === item.time;
  const submit = async () => {
    if (sameSlot) { toast.error(t("church.move_same_slot", "Choisis une autre heure ou un autre jour.")); return; }
    setBusy(true);
    try {
      const body = { day: Number(item.day), time: item.time, reason: reason.trim() || null, notify: notifyMembers };
      const { data } = moving
        ? await api.post(`/churches/${churchId}/services/move`, { ...body, new_date: newDate, new_time: newTime })
        : await api.post(`/churches/${churchId}/services/cancel`, body);
      const c = data.cancellation || {};
      const short = moving ? `${fmtDM(c.new_date)} ${c.new_time || ""}`.trim() : (c.date ? fmtDM(c.date) : dateLabel);
      const key = moving ? (data.notified ? "church.moved_toast_notified" : "church.moved_toast") : (data.notified ? "church.cancelled_toast_notified" : "church.cancelled_toast");
      const fb = moving ? (data.notified ? "Culte déplacé au {date} · {n} membre(s) prévenu(s) ✅" : "Culte déplacé au {date} ✅") : (data.notified ? "Culte du {date} annulé · {n} membre(s) prévenu(s) ✅" : "Culte du {date} annulé ✅");
      toast.success(t(key, fb).replace("{date}", short).replace("{n}", String(data.notified || 0)));
      onDone?.(c);
      onClose();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const accent = moving ? AMBER : RED;
  const Icon = moving ? CalendarClock : CalendarX2;
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="church-cancel-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Icon size={18} style={{ color: accent }} /> {moving ? t("church.move_service_title", "Déplacer ce culte ?") : t("church.cancel_service_title", "Annuler ce culte ?")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-full" style={{ background: "var(--yalola-surface-alt)" }} role="tablist" data-testid="church-service-mode">
            {[["cancel", t("church.mode_cancel", "Annuler"), CalendarX2], ["move", t("church.mode_move", "Déplacer"), CalendarClock]].map(([k, label, I]) => (
              <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`church-service-mode-${k}`}
                className="rounded-full py-1.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
                style={mode === k ? { background: k === "move" ? AMBER : RED, color: "white" } : { color: "var(--yalola-text-muted)" }}>
                <I size={14} /> {label}
              </button>
            ))}
          </div>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-cancel-service-hint">
            {(moving
              ? t("church.move_service_hint", "Seul le culte « {title} » du {date} à {time} est déplacé : les autres semaines ne changent pas. Le rappel automatique suivra la nouvelle date.")
              : t("church.cancel_service_hint", "Seul le culte « {title} » du {date} à {time} est annulé : les autres semaines ne changent pas. Le rappel automatique de ce jour-là ne sera pas envoyé."))
              .replace("{title}", title).replace("{date}", dateLabel).replace("{time}", item.time)}
          </p>
          {moving && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold" htmlFor="church-move-date">{t("church.move_new_date", "Nouvelle date")}</label>
                <Input id="church-move-date" type="date" value={newDate} min={localDateIso(new Date())} onChange={(e) => setNewDate(e.target.value)} className="mt-1 w-full" data-testid="church-move-date" />
              </div>
              <div>
                <label className="text-xs font-semibold" htmlFor="church-move-time">{t("church.move_new_time", "Nouvelle heure")}</label>
                <Input id="church-move-time" type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="mt-1 w-full" data-testid="church-move-time" />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold" htmlFor="church-cancel-reason">{t("church.cancel_reason", "Raison (facultatif, visible par les membres)")}</label>
            <Textarea id="church-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value.slice(0, 200))} rows={2} className="mt-1" data-testid="church-cancel-reason" />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1.5"><Bell size={14} /> {t("church.cancel_notify", "Prévenir les membres par notification")}</div>
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{moving ? t("church.move_notify_hint", "Une seule notification « Culte déplacé » aux membres qui suivent l'église.") : t("church.cancel_notify_hint", "Une seule notification « Culte annulé » aux membres qui suivent l'église.")}</div>
            </div>
            <Switch checked={notifyMembers} onCheckedChange={setNotifyMembers} data-testid="church-cancel-notify" />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={busy} data-testid="church-cancel-service-back">{t("common.back", "Retour")}</Button>
          <Button className="rounded-full" style={{ background: accent, color: "white" }} onClick={submit} disabled={busy || (moving && (!newDate || !newTime))} data-testid="church-cancel-service-confirm">
            <Icon size={14} className="mr-1" /> {moving ? t("church.move_confirm", "Déplacer le culte") : t("church.cancel_confirm", "Annuler le culte")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Encart « Culte annulé » (rouge) / « Culte déplacé » (ambre) pour tous, avec « Rétablir » pour les responsables.
export const ServiceCancellationsNotice = ({ churchId, cancellations, canEdit, onRestored }) => {
  const { t, lang } = useApp();
  const [busy, setBusy] = useState("");
  if (!cancellations?.length) return null;
  const restore = async (c) => {
    setBusy(c.id);
    try {
      await api.delete(`/churches/${churchId}/services/cancel/${c.id}`);
      toast.success(t("church.restored_toast", "Culte rétabli ✅"));
      onRestored?.(c);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="church-service-cancelled-notice">
      {cancellations.map((c) => {
        const moved = c.kind === "moved";
        const accent = moved ? AMBER : RED;
        const Icon = moved ? CalendarClock : CalendarX2;
        const line = moved
          ? t("church.service_moved_line", "« {title} » du {date} à {time} est déplacé au {new_date} à {new_time}.").replace("{new_date}", fmtIsoDate(c.new_date, lang)).replace("{new_time}", c.new_time || "")
          : t("church.service_cancelled_line", "« {title} » du {date} à {time} n'aura pas lieu.");
        return (
          <div key={c.id} className="yalola-card p-4 flex items-start justify-between gap-3 flex-wrap" style={moved ? { borderColor: "#FCD34D", background: "#FFFBEB" } : { borderColor: "#FCA5A5", background: "#FEF2F2" }} data-testid={`church-cancellation-${c.id}`}>
            <div className="flex items-start gap-3 min-w-0">
              <div className="shrink-0 h-9 w-9 rounded-full grid place-items-center" style={{ background: moved ? "#FEF3C7" : "#FEE2E2", color: accent }}><Icon size={18} /></div>
              <div className="min-w-0">
                <div className="text-sm font-bold" style={{ color: accent }}>{moved ? t("church.service_moved_notice", "Culte déplacé") : t("church.service_cancelled_notice", "Culte annulé")}</div>
                <div className="text-sm">{line.replace("{title}", c.title || t("church.service", "Culte")).replace("{date}", fmtIsoDate(c.date, lang)).replace("{time}", c.time)}</div>
                {c.reason && <div className="text-xs italic mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`church-cancellation-reason-${c.id}`}>« {c.reason} »</div>}
                {canEdit && c.notified && <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><Bell size={11} /> {t("church.members_notified", "Membres prévenus")}</div>}
              </div>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => restore(c)} disabled={busy === c.id} data-testid={`church-restore-service-${c.id}`}>
                <RotateCcw size={14} className="mr-1" /> {t("church.restore_service", "Rétablir")}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
